// src/pages/admin/AdminEnrolments.tsx
// Route: /admin/enrolments

import { useState, useMemo } from "react";
import {
  useAdminCompleteEnrolmentMutation,
  useAdminConfirmEnrolmentMutation,
  useAdminGetEnrolmentsQuery,
  useAdminUpdateProgressMutation,
  useAdminWithdrawEnrolmentMutation,
  type Enrolment,
} from "../../app/api/enrolmentApi";
import type { EnrolStatus, PayStatus } from "../../app/api/parentApi";
import {
  PageHeader,
  Skeleton,
  EmptyState,
  StatCard,
  TableContainer,
  Th,
  Td,
  Avatar,
  Pagination,
  SearchInput,
  Select,
  Button,
  EnrolChip,
  PayChip,
  BillingChip,
  formatNaira,
  formatDate,
  trackConfig,
} from "./AdminShared";

import { Check, CreditCard, Hourglass, Sparkles } from "lucide-react";
/* ─── DETAIL DRAWER ──────────────────────────────────────────────── */

function EnrolmentDetailDrawer({
  enrolment,
  onClose,
}: {
  enrolment: Enrolment;
  onClose: () => void;
}) {
  const [confirm, { isLoading: confirming }] =
    useAdminConfirmEnrolmentMutation();
  const [withdraw, { isLoading: withdrawing }] =
    useAdminWithdrawEnrolmentMutation();
  const [complete, { isLoading: completing }] =
    useAdminCompleteEnrolmentMutation();
  const [updateProgress, { isLoading: updating }] =
    useAdminUpdateProgressMutation();
  const [progressPct, setProgressPct] = useState(
    String(enrolment.progressPercent),
  );
  const [currentWeek, setCurrentWeek] = useState(String(enrolment.currentWeek));
  const [currentProject, setCurrentProject] = useState(
    enrolment.currentProject ?? "",
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [note, setNote] = useState("");
  const [actionError, setActionError] = useState("");

  const tc = trackConfig(enrolment.track);
  const child = enrolment.child;
  const parent = typeof enrolment.parent === "object" ? enrolment.parent : null;

  const handleConfirm = async () => {
    setActionError("");
    try {
      await confirm({ id: enrolment.id }).unwrap();
    } catch (err: any) {
      setActionError(err?.data?.error ?? "Failed to confirm");
    }
  };

  const handleWithdraw = async () => {
    if (!window.confirm("Withdraw this enrolment? This cannot be undone."))
      return;
    setActionError("");
    try {
      await withdraw({ id: enrolment.id, reason: note || undefined }).unwrap();
      onClose();
    } catch (err: any) {
      setActionError(err?.data?.error ?? "Failed to withdraw");
    }
  };

  const handleComplete = async () => {
    setActionError("");
    try {
      await complete({ id: enrolment.id }).unwrap();
      onClose();
    } catch (err: any) {
      setActionError(err?.data?.error ?? "Failed to complete");
    }
  };

  const handleUpdateProgress = async () => {
    setActionError("");
    try {
      await updateProgress({
        id: enrolment.id,
        currentWeek: Number(currentWeek),
        progressPercent: Number(progressPct),
        currentProject: currentProject || undefined,
      }).unwrap();
    } catch (err: any) {
      setActionError(err?.data?.error ?? "Update failed");
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#16213e]/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-[-8px_0_40px_rgba(27,45,94,0.12)] sm:max-w-[480px]"
        role="dialog"
        aria-modal
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#ebeff7] px-6 py-5">
          <div>
            <h2 className="text-lg font-bold text-[#16213e]">
              Enrolment detail
            </h2>
            <p className="mt-0.5 text-xs text-[#8189a0]">
              {enrolment.quarter} · {enrolment.academicYear}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl text-[#8189a0] hover:bg-[#f1f3f8]"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Status chips */}
          <div className="flex flex-wrap gap-2">
            <EnrolChip status={enrolment.status} />
            <PayChip status={enrolment.paymentStatus} />
            <BillingChip
              period={enrolment.billingPeriod}
              label={enrolment.billingLabel}
            />
          </div>

          {/* Student info */}
          {child && (
            <div className="rounded-[18px] border border-[#dde2ec] bg-[#f9fafc] overflow-hidden">
              <div className="px-4 py-3 border-b border-[#ebeff7]">
                <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                  Student
                </p>
              </div>
              <div className="px-4 py-3 flex items-center gap-3">
                <Avatar firstName={child.firstName} lastName={child.lastName} />
                <div>
                  <p className="text-sm font-bold text-[#16213e]">
                    {child.firstName} {child.lastName}
                  </p>
                  <p className="text-xs text-[#8189a0]">
                    Age {child.age} · {tc.label}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Parent info */}
          {parent && typeof parent === "object" && (
            <div className="rounded-[18px] border border-[#dde2ec] bg-[#f9fafc] overflow-hidden">
              <div className="px-4 py-3 border-b border-[#ebeff7]">
                <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                  Parent
                </p>
              </div>
              <div className="px-4 py-3">
                <p className="text-sm font-semibold text-[#16213e]">
                  {(parent as any).firstName} {(parent as any).lastName}
                </p>
                <p className="text-xs text-[#8189a0]">
                  {(parent as any).email}
                </p>
              </div>
            </div>
          )}

          {/* Billing breakdown */}
          <div className="rounded-[18px] border border-[#dde2ec] overflow-hidden">
            <div className="px-4 py-3 border-b border-[#ebeff7] bg-[#f9fafc]">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                Billing
              </p>
            </div>
            <div className="divide-y divide-[#f7f8fb] text-xs">
              {[
                { label: "Plan", value: enrolment.billingLabel ?? "—" },
                { label: "Months", value: `${enrolment.monthsCovered}` },
                {
                  label: "Base fee/mo",
                  value: formatNaira(enrolment.baseMonthlyFee),
                },
                { label: "Gross", value: formatNaira(enrolment.grossAmount) },
                {
                  label: "Discount",
                  value: enrolment.discountPercent
                    ? `-${enrolment.discountPercent}%`
                    : "None",
                },
                {
                  label: "Amount due",
                  value: formatNaira(enrolment.feeAmount),
                  bold: true,
                },
                {
                  label: "Next payment",
                  value: enrolment.nextPaymentLabel ?? "—",
                },
                {
                  label: "Confirmed on",
                  value: formatDate(enrolment.confirmedAt),
                },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex justify-between px-4 py-2.5"
                >
                  <span className="text-[#8189a0]">{row.label}</span>
                  <span
                    className={
                      row.bold
                        ? "font-bold text-[#16213e]"
                        : "font-semibold text-[#3b4560]"
                    }
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Progress update */}
          <div className="rounded-[18px] border border-[#dde2ec] overflow-hidden">
            <div className="px-4 py-3 border-b border-[#ebeff7] bg-[#f9fafc] flex items-center justify-between">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                Progress
              </p>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleUpdateProgress}
                disabled={updating}
              >
                {updating ? "Saving…" : "Save progress"}
              </Button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="mb-1.5 text-[12.5px] font-semibold text-[#8189a0]">
                    Current week
                  </p>
                  <input
                    type="number"
                    min="1"
                    max="18"
                    value={currentWeek}
                    onChange={(e) => setCurrentWeek(e.target.value)}
                    className="w-full rounded-xl border border-[#dde2ec] px-3 py-2 text-sm outline-none focus:border-[#1b2d5e]"
                  />
                </div>
                <div>
                  <p className="mb-1.5 text-[12.5px] font-semibold text-[#8189a0]">
                    Progress %
                  </p>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={progressPct}
                    onChange={(e) => setProgressPct(e.target.value)}
                    className="w-full rounded-xl border border-[#dde2ec] px-3 py-2 text-sm outline-none focus:border-[#1b2d5e]"
                  />
                </div>
              </div>
              <div>
                <p className="mb-1.5 text-[12.5px] font-semibold text-[#8189a0]">
                  Current project
                </p>
                <input
                  type="text"
                  value={currentProject}
                  onChange={(e) => setCurrentProject(e.target.value)}
                  placeholder="e.g. Scratch interactive story"
                  className="w-full rounded-xl border border-[#dde2ec] px-3 py-2 text-sm outline-none focus:border-[#1b2d5e]"
                />
              </div>
              {/* Progress bar preview */}
              <div className="h-2 overflow-hidden rounded-full bg-[#ebeff7]">
                <div
                  className="h-full rounded-full bg-[#1b2d5e] transition-all"
                  style={{ width: `${Math.min(100, Number(progressPct))}%` }}
                />
              </div>
            </div>
          </div>

          {actionError && (
            <div className="rounded-[14px] border border-[#fde0d9] bg-[#fff5f3] px-4 py-3 text-xs text-[#b94a3a]">
              {actionError}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex-shrink-0 border-t border-[#ebeff7] px-6 py-5 space-y-2.5">
          {enrolment.status === "pending" &&
            enrolment.paymentStatus === "paid" && (
              <Button
                variant="primary"
                className="w-full"
                onClick={handleConfirm}
                disabled={confirming}
              >
                {confirming ? "Confirming…" : <><Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> Confirm enrolment</>}
              </Button>
            )}
          {enrolment.status === "confirmed" && (
            <Button
              variant="secondary"
              className="w-full"
              onClick={handleComplete}
              disabled={completing}
            >
              {completing ? "Completing…" : "Mark quarter complete"}
            </Button>
          )}
          {enrolment.status !== "withdrawn" &&
            enrolment.status !== "completed" && (
              <Button
                variant="danger"
                className="w-full"
                onClick={handleWithdraw}
                disabled={withdrawing}
              >
                {withdrawing ? "Withdrawing…" : "Withdraw enrolment"}
              </Button>
            )}
        </div>
      </div>
    </>
  );
}

/* ─── TABLE ROW ──────────────────────────────────────────────────── */

function EnrolRow({
  enrolment,
  onClick,
}: {
  enrolment: Enrolment;
  onClick: () => void;
}) {
  const child = enrolment.child;
  const tc = trackConfig(enrolment.track);
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer border-b border-[#f7f8fb] transition hover:bg-[#f9fafc]"
    >
      <Td>
        <div className="flex items-center gap-2.5">
          {child && (
            <Avatar
              firstName={child.firstName}
              lastName={child.lastName}
              size="sm"
            />
          )}
          <div>
            <p className="font-semibold text-[#16213e]">
              {child ? `${child.firstName} ${child.lastName}` : "—"}
            </p>
            <p className="text-[12.5px] text-[#8189a0]">Age {child?.age}</p>
          </div>
        </div>
      </Td>
      <Td>
        <span
          className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold"
          style={{
            background: tc.bg,
            color: tc.text,
            border: `1px solid ${tc.border}`,
          }}
        >
          {tc.label}
        </span>
      </Td>
      <Td>
        <p className="font-medium">
          {enrolment.quarter} · {enrolment.academicYear}
        </p>
        <p className="text-[12.5px] text-[#8189a0]">
          Week {enrolment.currentWeek}
        </p>
      </Td>
      <Td>
        <BillingChip
          period={enrolment.billingPeriod}
          label={enrolment.billingLabel}
        />
      </Td>
      <Td>
        <EnrolChip status={enrolment.status} />
      </Td>
      <Td>
        <PayChip status={enrolment.paymentStatus} />
      </Td>
      <Td>
        <p className="font-semibold">{formatNaira(enrolment.feeAmount)}</p>
        {enrolment.discountPercent > 0 && (
          <p className="text-[11.5px] text-[#6cc2a0] font-semibold">
            {enrolment.discountPercent}% off
          </p>
        )}
      </Td>
      <Td>
        <svg className="h-4 w-4 text-[#c9cfe0]" viewBox="0 0 16 16" fill="none">
          <path
            d="M6 4l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Td>
    </tr>
  );
}

/* ─── ROOT PAGE ──────────────────────────────────────────────────── */

export default function AdminEnrolments() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [payStatus, setPayStatus] = useState("");
  const [track, setTrack] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Enrolment | null>(null);

  const { data, isLoading, isError, refetch } = useAdminGetEnrolmentsQuery({
    page,
    limit: 25,
    ...(status ? { status: status as EnrolStatus } : {}),
    ...(payStatus ? { paymentStatus: payStatus as PayStatus } : {}),
    ...(track ? { track: track as any } : {}),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const enrolments = data?.data ?? [];

  const filtered = useMemo(() => {
    if (!search.trim()) return enrolments;
    const q = search.trim().toLowerCase();
    return enrolments.filter((e) => {
      const child = e.child;
      const name = child
        ? `${child.firstName} ${child.lastName}`.toLowerCase()
        : "";
      return (
        name.includes(q) ||
        e.quarter.toLowerCase().includes(q) ||
        e.academicYear.includes(q)
      );
    });
  }, [enrolments, search]);

  // Stats from current page
  const stats = useMemo(
    () => ({
      total: data?.total ?? 0,
      confirmed: enrolments.filter((e) => e.status === "confirmed").length,
      pending: enrolments.filter((e) => e.status === "pending").length,
      revenue: enrolments
        .filter((e) => e.paymentStatus === "paid")
        .reduce((s, e) => s + e.feeAmount, 0),
    }),
    [enrolments, data],
  );

  if (isError)
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-center">
        <p className="text-sm font-semibold text-[#16213e]">
          Failed to load enrolments
        </p>
        <Button variant="primary" onClick={refetch}>
          Retry
        </Button>
      </div>
    );

  return (
    <>
      <PageHeader
        title="Enrolments"
        subtitle="Manage all student enrolments across every track and quarter."
      />

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[72px] rounded-[20px]" />
            ))
          : [
              {
                label: "Total",
                value: String(stats.total),
                sub: "all time",
                icon: Sparkles,
                color: "#1b2d5e",
                bg: "#ebeff7",
              },
              {
                label: "Confirmed",
                value: String(stats.confirmed),
                sub: "on this page",
                icon: Check,
                color: "#6cc2a0",
                bg: "#edfaf4",
              },
              {
                label: "Pending",
                value: String(stats.pending),
                sub: "need attention",
                icon: Hourglass,
                color: "#f0a050",
                bg: "#fff8ee",
              },
              {
                label: "Revenue",
                value: formatNaira(stats.revenue),
                sub: "paid enrolments",
                icon: CreditCard,
                color: "#4a44b0",
                bg: "#e8ecf5",
              },
            ].map((s) => <StatCard key={s.label} {...s} />)}
      </div>

      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          placeholder="Search by name, quarter…"
        />
        <Select
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
          <option value="withdrawn">Withdrawn</option>
        </Select>
        <Select
          value={payStatus}
          onChange={(v) => {
            setPayStatus(v);
            setPage(1);
          }}
        >
          <option value="">All payments</option>
          <option value="pending">Payment pending</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
        </Select>
        <Select
          value={track}
          onChange={(v) => {
            setTrack(v);
            setPage(1);
          }}
        >
          <option value="">All tracks</option>
          <option value="foundation_builders">Foundation (6–9)</option>
          <option value="explorers">Explorers (9–12)</option>
          <option value="innovators">Innovators (13–18)</option>
        </Select>
        <p className="text-xs text-[#8189a0]">{filtered.length} records</p>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title="No enrolments found"
          subtitle="Try adjusting your filters."
        />
      ) : (
        <TableContainer>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-[#ebeff7]">
              <tr>
                {[
                  "Student",
                  "Track",
                  "Quarter",
                  "Plan",
                  "Status",
                  "Payment",
                  "Fee",
                  "",
                ].map((h) => (
                  <Th key={h}>{h}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <EnrolRow
                  key={e.id}
                  enrolment={e}
                  onClick={() => setSelected(e)}
                />
              ))}
            </tbody>
          </table>
          <Pagination
            page={data?.page ?? 1}
            pages={data?.pages ?? 1}
            total={data?.total ?? 0}
            count={filtered.length}
            onPage={(p) => {
              setPage(p);
              window.scrollTo({ top: 0 });
            }}
          />
        </TableContainer>
      )}

      {selected && (
        <EnrolmentDetailDrawer
          enrolment={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
